import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Input, Button, Alert } from 'antd';
import { LoginOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { api, HttpError } from '../api';
import { t } from '../i18n';
import { Logo } from '../components/Logo';
import { HintTooltip as Tooltip } from '../components/HintTooltip';


export function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpRequired, setTotpRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lockUntil, setLockUntil] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const sso = useQuery({
    queryKey: ['sso-status'],
    queryFn: api.ssoStatus,
    staleTime: 5 * 60_000,
    retry: false,
  });
  // Public endpoint — lets the login footer show the running version like the
  // in-app footer does (no auth needed).
  const serverInfo = useQuery({ queryKey: ['server-info'], queryFn: api.getServerInfo });

  useEffect(() => {
    if (lockUntil === null) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [lockUntil]);

  const lockSecondsLeft =
    lockUntil === null ? 0 : Math.max(0, Math.ceil((lockUntil - Date.now()) / 1000));
  void tick;
  useEffect(() => {
    if (lockUntil !== null && lockSecondsLeft === 0) {
      setLockUntil(null);
      setError(null);
    }
  }, [lockUntil, lockSecondsLeft]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (lockSecondsLeft > 0) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.loginWithTotp(username, password, totpRequired ? totpCode : undefined);
      qc.setQueryData(['me'], { user: r.user });
      navigate('/sessions');
    } catch (e) {
      if (e instanceof HttpError) {
        if (e.status === 429) {
          try {
            const parsed = JSON.parse(e.detail) as { retryAfterSec?: number };
            setLockUntil(Date.now() + (parsed.retryAfterSec ?? 900) * 1000);
            setError(null);
            return;
          } catch {
            /* ignore */
          }
        }
        if (e.status === 401) {
          try {
            const parsed = JSON.parse(e.detail) as { totpRequired?: boolean };
            if (parsed.totpRequired) {
              setTotpRequired(true);
              setError(totpCode ? t('totpFailed') : null);
              return;
            }
          } catch {
            /* ignore */
          }
        }
      }
      setError(t('loginBadCredentials'));
    } finally {
      setBusy(false);
    }
  };

  const fmtSeconds = (s: number): string => {
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${mm}:${String(ss).padStart(2, '0')}`;
  };

  const label = (text: string) => (
    <div style={{ margin: '12px 0 4px', fontWeight: 600 }}>{text}</div>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        minHeight: '100vh',
        padding: 24,
        background: 'linear-gradient(135deg,#0d1220,#1a2340 55%,#0d1220)',
      }}
    >
      <Logo size={64} showWordmark wordmarkColor="#e8ecf4" />
      <Card style={{ width: 380 }}>
        <div
          style={{
            textAlign: 'center',
            marginBottom: 12,
            fontSize: 20,
            fontWeight: 600,
          }}
        >
          {t('loginTitle')}
        </div>
        <form onSubmit={onSubmit}>
          {label(t('loginUsername'))}
          <Input
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            size="large"
          />
          {label(t('loginPassword'))}
          <Input.Password
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            size="large"
          />
          {totpRequired && (
            <>
              {label(t('totpCodeInput'))}
              <Input
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                inputMode="numeric"
                autoFocus
                size="large"
                style={{ letterSpacing: 6, textAlign: 'center', fontSize: 18 }}
              />
            </>
          )}
          <Button
            type="primary"
            htmlType="submit"
            block
            size="large"
            icon={<LoginOutlined />}
            loading={busy}
            disabled={lockSecondsLeft > 0}
            style={{ marginTop: 20 }}
          >
            {lockSecondsLeft > 0 ? `${t('loginLocked')} ${fmtSeconds(lockSecondsLeft)}` : t('loginSubmit')}
          </Button>
          {sso.data?.enabled && (
            <Button
              block
              size="large"
              icon={<SafetyCertificateOutlined />}
              style={{ marginTop: 10 }}
              onClick={() => {
                window.location.href = '/api/auth/sso/start';
              }}
            >
              {t('loginSsoBtn')}
            </Button>
          )}
          {error && <Alert type="error" showIcon style={{ marginTop: 14 }} message={error} />}
          {lockSecondsLeft > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 14 }}
              message={t('loginLockedExplain', { time: fmtSeconds(lockSecondsLeft) })}
            />
          )}
        </form>
      </Card>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          fontSize: 12,
          color: '#8b95ad',
        }}
      >
        <span>© 2026 DDiskSatellite</span>
        {serverInfo.data && (
          <>
            <span aria-hidden>·</span>
            <Tooltip title={`commit ${serverInfo.data.commit}`}>
              <span>v{serverInfo.data.version}</span>
            </Tooltip>
            <span aria-hidden>·</span>
            <Tooltip title={`commit ${serverInfo.data.commit}`}>
              <span>{serverInfo.data.commit}</span>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
}
